/**
 * Lithuanian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Lithuanian at a mathematician's register. **Warrant → «Pagrindimas»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / few / many / other (`one` = …1 except 11; `few` = 2–9 except 12–19;
 * `many` = the decimal form; `other` = 0, 10–20), with «apribojimas» declined accordingly, so
 * `frameActive` declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const LT_CATALOG = {
    locale: 'lt',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Tyrimas',
        [P2.sectionClaims]: 'Teiginiai',
        [P2.sectionIntegrity]: 'Vientisumas',
        [P2.axisWarrantLabel]: 'Pagrindimas',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirinis',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Apibrėžimas} interp {Interpretacija} testable {Patikrinamas} tested {Patikrintas} refuted {Paneigtas} program {Tyrimų programa} philos {Filosofinis} other {Nežinomas}}',
        [P2.axisModalLabel]: 'Modalinis',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Stiprumo lygis {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Tiriamas',
        [P2.stateRefuted]: 'Paneigtas',
        [P2.stateOutside]: 'Už srities ribų',
        [P2.verdictConflict]: 'Konfliktuoja su {constraints}',
        [P2.verdictOutside]: 'Reikalauja {missing}',
        [P2.lossRegisterPlain]: 'Supaprastinta į bendrinę kalbą — dalis tikslumo praleista.',
        [P2.lossRegisterKid]: 'Supaprastinta jaunesniems skaitytojams — daug detalių praleista.',
        [P2.lossProfileDetail]: 'Visas profilis Pagrindimas / Empirinis / Modalinis čia apibendrintas.',
        [P2.lossConflictDetail]: 'Konflikto detalės šiame rodinyje sutrumpintos.',
        [P2.laneVerified]: 'Patvirtinta',
        [P2.laneUnverified]: 'Nepatvirtinta',
        [P2.laneConflict]: 'Konfliktas įrodytas',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'Šviesa išlinksta gravitaciniame lauke',
        [P2.constraintParallelPostulate]: 'Lygiagrečių postulatas',
        [P2.claimScalarGravity]: 'Skaliarinė gravitacijos teorija',
        [P2.claimNonEuclidean]: 'Neeuklidinė geometrija',
        [P2.claimSubstrateSr]: 'Specialiosios reliatyvumo teorijos substrato interpretacija',
        [P2.edgeExtObservation]: 'Išorinis stebėjimas',
        [P2.edgeResourceSample]: 'Bendrinamas skaičiavimo išteklius',
        [P2.edgeHeuristicSample]: 'Euristinis nurodymas',
        [P2.edgeSharedSample]: 'Bendras pagrindas',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Apribojimai',
        [P2.sectionEdges]: 'Briaunos',
        [P2.sectionFrame]: 'Rėmas',
        [P2.sectionExposure]: 'Atvirumas',
        [P2.statusAdoptedFormal]: 'Priimta (formaliai)',
        [P2.statusAdoptedInterpretive]: 'Priimta (interpretaciniu būdu)',
        [P2.statusSuspended]: 'Sustabdyta',
        [P2.edgeEffectConstraint]: 'Sudaro apribojimą (gali paneigti)',
        [P2.edgeEffectResource]: 'Išteklius — neturi įtakos verdiktams',
        [P2.edgeEffectHeuristic]: 'Euristika — neturi įtakos verdiktams',
        [P2.edgeEffectShared]: 'Bendras pagrindas — neturi įtakos verdiktams',
        [P2.frameActive]: '{count, plural, =0 {Nėra aktyvių apribojimų} one {# aktyvus apribojimas} few {# aktyvūs apribojimai} many {# aktyvaus apribojimo} other {# aktyvių apribojimų}}',
        [P2.exposureNone]: 'Nėra interpretacinių priklausomybių',
        [P2.exposureDep]: 'Priklauso nuo {id}',
        [P2.laneTrackMachine]: 'Mašina',
        [P2.laneTrackExpert]: 'Ekspertas',
        [P2.laneTrackCommunity]: 'Bendruomenė',
        [P2.expertAudited]: 'Audituota',
        [P2.expertInProgress]: 'Vykdoma',
        [P2.expertNotReviewed]: 'Neperžiūrėta',
        [P2.communityFlags]: 'Yra žymų',
        [P2.communityNone]: 'Žymų nėra',
        [P2.controlChallenge]: 'Užginčyti',
        [P2.controlActivate]: 'Aktyvinti',
        [P2.controlMakeInterpretive]: 'Padaryti interpretacinį',
        [P2.controlImportEdge]: 'Importuoti briauną',
        [P2.controlPromote]: 'Paaukštinti',
        [P2.controlUndo]: 'Anuliuoti',
        [P2.controlRedo]: 'Pakartoti',
        [P2.controlRegister]: 'Registruoti',
        [P2.promoteRejected]: 'Paaukštinimas atmestas',
        [P2.promoteAccepted]: 'Paaukštinimas priimtas',
        [P2.promoteReasonRefutationTerminal]: 'Įrodymas negali išgydyti paneigimo.',
        [P2.promoteReasonKeystone]: 'Kertinis paaukštinimas reikalauja empirinės paramos.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empirinis tipas nenustatytas.',
        [P2.promoteReasonModalNotFixed]: 'Modalinis laipsnis nenustatytas.',
        [P2.promoteReasonWarrantNotImproved]: 'Pagrindimo laipsnis nepagerėjo.',
        [P2.evidenceComputation]: "{engine} apskaičiavo {op} = {result} pagal {config}",
        [P2.lossNumericRounding]: "Rezultatas suapvalintas iki {dp} dešimtųjų",
    },
};
//# sourceMappingURL=lt.js.map